import type {
  Answer,
  AnswerValue,
  Assignment,
  Question,
  QuestionAggregate,
  SurveyResponse,
  UnansweredTarget,
} from "./contract";
import { isValidDate } from "./date";

/**
 * survey/UniQ 純ロジック (PRODUCT_DEV-574): 分岐判定・回答バリデーション・集計・未回答算出。
 * DDB/AWS 非依存。web/api で共用し vitest でカバーする。
 */

export interface ValidationResult {
  ok: boolean;
  error?: string;
}

/** radio 回答値から選択値(v)を取り出す。string もしくは {v,text} の両形に対応。 */
export const radioV = (value: AnswerValue | undefined): string | undefined => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && !Array.isArray(value))
    return value.v;
  return undefined;
};

/**
 * 対象顧客名は組み込みの顧客フィールドで表示・入力するため、同名の重複設問を除外する
 * (PRODUCT_DEV-574 項目8/9)。web(表示/回答フォーム)と api(送信検証)で同一述語を共用し、
 * クライアント/サーバの検証対象がずれないようにする。
 */
const CUSTOMER_NAME_LABELS = new Set(["対象顧客", "対象顧客名"]);
export const isCustomerNameLabelQuestion = (q: Question): boolean =>
  CUSTOMER_NAME_LABELS.has(q.label.trim());
export const stripCustomerNameQuestions = (questions: Question[]): Question[] =>
  questions.filter((q) => !isCustomerNameLabelQuestion(q));

// ── 表示条件 (phase) 文法 ────────────────────────────────────────────────────
// "always" | cond(&&cond)*、cond = "<parentId>:<token>|<token>"。
// cond 内の | は OR、cond 間の && は AND (すべて満たすとき表示)。
// 親にできる設問タイプと token の意味 (PRODUCT_DEV-627):
//   radio/checkbox … token = 選択肢値 v。回答が一致(radio)/含む(checkbox)なら真。
//   scale          … token = 数値述語 ">=N" "<=N" ">N" "<N" "=N" もしくは範囲 "A..B"(両端含む)。
//   date           … token = 日付述語 ">=YYYY/MM/DD" 等・範囲 "A..B"。正準日付は辞書順=日付順。
//   text           … 分岐親にできない (離散値なし・validateQuestionDefs で拒否)。
// token 種別は先頭演算子/".." の有無で判定する。選択肢値は 8桁hex/定数のみで演算子・".." を
// 含まないため後方互換。旧単一 cond 形式・旧 radio 限定 phase はそのまま有効 (移行不要)。

/** 1つの表示条件: 親設問 parentId の回答が values (token 群) のいずれかを満たすこと。 */
export interface PhaseCond {
  parentId: string;
  values: string[];
}

/** phase に含められる条件数の上限 (builder/バリデーション共通)。 */
export const MAX_PHASE_CONDS = 5;

/** phase 文字列 → 条件配列。"always"/空は null (=常時表示)。全消費者はこれ経由で読む。 */
export const parsePhase = (phase: string): PhaseCond[] | null => {
  if (phase === "always" || phase.trim() === "") return null;
  return phase.split("&&").map((c) => {
    const [parentId, valsRaw] = c.split(":");
    return {
      parentId: parentId ?? "",
      values: (valsRaw ?? "").split("|").filter(Boolean),
    };
  });
};

/** 条件配列 → phase 文字列 (空配列は "always")。builder の編集結果を書き戻す。 */
export const stringifyPhase = (conds: PhaseCond[]): string =>
  conds.length === 0
    ? "always"
    : conds.map((c) => `${c.parentId}:${c.values.join("|")}`).join("&&");

/**
 * phase から、削除された親設問を参照する条件を剥がす (全条件が消えたら "always")。
 * 設問削除 (builder) とプリセット除去 (withPreset) の孤立分岐カスケードの正本。
 */
export const stripPhaseParents = (
  phase: string,
  removedParentIds: ReadonlySet<string>,
): string => {
  const conds = parsePhase(phase);
  if (!conds) return phase;
  const kept = conds.filter((c) => !removedParentIds.has(c.parentId));
  return kept.length === conds.length ? phase : stringifyPhase(kept);
};

/**
 * phase から、親設問 parentId の削除された選択肢値を剥がす。values が空になった条件は
 * 条件ごと除去する (不可視の残留値が validateQuestionDefs の保存拒否を招くのを防ぐ)。
 */
export const stripPhaseOptionValues = (
  phase: string,
  parentId: string,
  removedValues: readonly string[],
): string => {
  const conds = parsePhase(phase);
  if (!conds) return phase;
  const removed = new Set(removedValues);
  let changed = false;
  const kept: PhaseCond[] = [];
  for (const c of conds) {
    if (c.parentId !== parentId) {
      kept.push(c);
      continue;
    }
    const values = c.values.filter((v) => !removed.has(v));
    if (values.length === c.values.length) {
      kept.push(c);
      continue;
    }
    changed = true;
    if (values.length > 0) kept.push({ ...c, values });
  }
  return changed ? stringifyPhase(kept) : phase;
};

// ── トークン述語 (scale/date の比較・範囲) ────────────────────────────────────
export type ComparisonOp = ">=" | "<=" | ">" | "<" | "=";
export interface CmpPredicate {
  kind: "cmp";
  op: ComparisonOp;
  operand: string;
}
export interface RangePredicate {
  kind: "range";
  lo: string;
  hi: string;
}
export type TokenPredicate = CmpPredicate | RangePredicate;

// ">=" "<=" は ">" "<" "=" より先に判定する (2文字演算子の取りこぼし防止)。
const COMPARISON_OPS: readonly ComparisonOp[] = [">=", "<=", ">", "<", "="];

/**
 * token を比較/範囲述語として解釈する。素の一致値 (radio/checkbox の選択肢値・素の日付) は null。
 * 空オペランド (">=", "..5", "5..") は「不正な述語」として null を返し、matcher/validator は
 * これを一致にも成立にもしない (Number("")===0 の取り違え防止)。builder/matcher/validator/
 * phaseLabel が同じ定義を使うため純関数としてここに集約する。
 */
export const parseTokenPredicate = (token: string): TokenPredicate | null => {
  const dot = token.indexOf("..");
  if (dot >= 0) {
    const lo = token.slice(0, dot);
    const hi = token.slice(dot + 2);
    if (lo === "" || hi === "") return null;
    return { kind: "range", lo, hi };
  }
  for (const op of COMPARISON_OPS) {
    if (token.startsWith(op)) {
      const operand = token.slice(op.length);
      return operand === "" ? null : { kind: "cmp", op, operand };
    }
  }
  return null;
};

const isIntToken = (s: string): boolean => /^-?\d+$/.test(s);

/** scale (number) 回答に対する 1 token の判定。空/非整数オペランドは false。 */
const matchScaleToken = (token: string, n: number): boolean => {
  const p = parseTokenPredicate(token);
  if (!p) return isIntToken(token) && Number(token) === n; // 素の数値一致
  if (p.kind === "range") {
    if (!isIntToken(p.lo) || !isIntToken(p.hi)) return false;
    return Number(p.lo) <= n && n <= Number(p.hi);
  }
  if (!isIntToken(p.operand)) return false;
  const v = Number(p.operand);
  if (p.op === ">=") return n >= v;
  if (p.op === "<=") return n <= v;
  if (p.op === ">") return n > v;
  if (p.op === "<") return n < v;
  return n === v; // "="
};

/** date (正準 "YYYY/MM/DD") 回答に対する 1 token の判定。ゼロ埋め正準なので辞書順=日付順。 */
const matchDateToken = (token: string, s: string): boolean => {
  if (!isValidDate(s)) return false;
  const p = parseTokenPredicate(token);
  if (!p) return token === s; // 素の日付一致
  if (p.kind === "range") {
    if (!isValidDate(p.lo) || !isValidDate(p.hi)) return false;
    return p.lo <= s && s <= p.hi;
  }
  if (!isValidDate(p.operand)) return false;
  if (p.op === ">=") return s >= p.operand;
  if (p.op === "<=") return s <= p.operand;
  if (p.op === ">") return s > p.operand;
  if (p.op === "<") return s < p.operand;
  return s === p.operand; // "="
};

/**
 * 1 条件 (cond) が回答値を満たすか。cond 内 token は OR。親の型は渡さず、回答値の
 * ランタイム型で判定する: 回答形は設問型の全単射 (number⇔scale, array⇔checkbox,
 * {v,text}/string⇔radio, string⇔date)。text は親にできないため string=radio or date に限る。
 * 未回答 (undefined/null) は非表示 (fail-closed)。0 を falsy 扱いしない (typeof で判定)。
 */
const condMatches = (
  values: string[],
  answer: AnswerValue | undefined,
): boolean => {
  if (answer === undefined || answer === null) return false;
  if (Array.isArray(answer)) return values.some((t) => answer.includes(t)); // checkbox
  if (typeof answer === "object") return values.some((t) => t === answer.v); // radio {v,text}
  if (typeof answer === "number")
    return values.some((t) => matchScaleToken(t, answer)); // scale
  // string: radio 値 (素の一致) or date (比較/範囲・素の日付)
  return values.some((t) =>
    parseTokenPredicate(t) ? matchDateToken(t, answer) : t === answer,
  );
};

/** 設問が現在表示対象か (分岐 phase の判定)。全条件を満たすとき表示。 */
export const isQuestionVisible = (
  q: Question,
  byId: Map<string, AnswerValue>,
): boolean => {
  const conds = parsePhase(q.phase);
  if (!conds) return true;
  return conds.every((c) => condMatches(c.values, byId.get(c.parentId)));
};

/** 表示中の設問だけを返す (分岐適用)。 */
export const visibleQuestions = (
  questions: Question[],
  answers: Answer[],
): Question[] => {
  const byId = new Map(answers.map((a) => [a.questionId, a.value]));
  return questions.filter((q) => isQuestionVisible(q, byId));
};

/**
 * 実効的に表示中の設問 ID 集合 (再帰版可視判定)。
 * isQuestionVisible は親設問の値だけを見るが、親自身が非表示の場合その値は
 * 無効とみなすべきなので、親の可視性まで連鎖して判定する (親切替→孫非表示に対応)。
 * 表示条件の親が切り替わった際の値クリア (PRODUCT_DEV-574) で使用。
 */
export const effectiveVisibleIds = (
  questions: Question[],
  byId: Map<string, AnswerValue>,
): Set<string> => {
  const byQid = new Map(questions.map((q) => [q.questionId, q]));
  const memo = new Map<string, boolean>();
  const vis = (q: Question): boolean => {
    const hit = memo.get(q.questionId);
    if (hit !== undefined) return hit;
    memo.set(q.questionId, false); // 循環参照は非表示扱いで打ち切る
    let ok = true;
    const conds = parsePhase(q.phase);
    if (conds) {
      ok =
        conds.every((c) => {
          const parent = byQid.get(c.parentId);
          return !!parent && vis(parent);
        }) && isQuestionVisible(q, byId);
    }
    memo.set(q.questionId, ok);
    return ok;
  };
  return new Set(questions.filter(vis).map((q) => q.questionId));
};

/**
 * 設問定義に対して回答が妥当か検証する (分岐・型整合・range・required)。
 * opts.skipRequired=true で必須チェックのみ免除する (④: 音声モードの即記録用。
 * 型/選択肢/範囲の妥当性チェックは常に行う)。
 */
export const validateAnswers = (
  questions: Question[],
  answers: Answer[],
  opts?: { skipRequired?: boolean },
): ValidationResult => {
  const byId = new Map(answers.map((a) => [a.questionId, a.value]));
  const required = (q: Question): boolean => q.required && !opts?.skipRequired;

  for (const q of questions) {
    if (!isQuestionVisible(q, byId)) continue;
    const v = byId.get(q.questionId);
    const missing = v === undefined || v === null;

    switch (q.type) {
      case "radio": {
        if (missing) {
          if (required(q))
            return { ok: false, error: `required unanswered: ${q.questionId}` };
          break;
        }
        const rv = radioV(v);
        const opt = q.options.find((o) => o.v === rv);
        if (!opt)
          return { ok: false, error: `invalid radio option: ${q.questionId}` };
        if (opt.withText && required(q)) {
          const txt =
            v && typeof v === "object" && !Array.isArray(v) ? v.text : "";
          if (!txt || txt.trim() === "")
            return { ok: false, error: `radio text required: ${q.questionId}` };
        }
        break;
      }
      case "checkbox": {
        const arr = Array.isArray(v) ? v : [];
        if (required(q) && arr.length === 0)
          return { ok: false, error: `required unanswered: ${q.questionId}` };
        if (arr.some((x) => !q.options.some((o) => o.v === x)))
          return { ok: false, error: `invalid checkbox value: ${q.questionId}` };
        break;
      }
      case "date": {
        const s = typeof v === "string" ? v : "";
        if (s.trim() === "") {
          if (required(q))
            return { ok: false, error: `required unanswered: ${q.questionId}` };
          break;
        }
        // 入力は UI のカレンダーピッカー限定。想定外の文字列 (AI 抽出・API 直叩き) は弾く。
        if (!isValidDate(s))
          return { ok: false, error: `invalid date: ${q.questionId}` };
        break;
      }
      case "text": {
        const s = typeof v === "string" ? v : "";
        if (required(q) && s.trim() === "")
          return { ok: false, error: `required unanswered: ${q.questionId}` };
        break;
      }
      case "scale": {
        if (missing) {
          if (required(q))
            return { ok: false, error: `required unanswered: ${q.questionId}` };
          break;
        }
        if (typeof v !== "number")
          return { ok: false, error: `scale requires number: ${q.questionId}` };
        if (v < q.scaleMin || v > q.scaleMax)
          return { ok: false, error: `scale out of range: ${q.questionId}` };
        break;
      }
    }
  }
  return { ok: true };
};

/** 実施完了 (表示中の必須設問がすべて埋まっているか)。 */
export const isComplete = (questions: Question[], answers: Answer[]): boolean =>
  validateAnswers(questions, answers).ok;

/**
 * ④ 音声モード(freerec/dialog)のみ submit の必須チェックを免除するか。
 * 未指定 (undefined) と tap は従来どおり必須検証あり — 既定は厳格側 (fail-closed)。
 */
export const shouldSkipRequired = (answerMode?: string): boolean =>
  answerMode === "freerec" || answerMode === "dialog";

/** scale 親の分岐 token が妥当か (整数の比較/範囲。範囲は lo<=hi。min/max 内は要求しない)。 */
const isValidScaleBranchToken = (token: string): boolean => {
  const p = parseTokenPredicate(token);
  if (!p) return isIntToken(token); // 素の整数一致は許容
  if (p.kind === "range")
    return isIntToken(p.lo) && isIntToken(p.hi) && Number(p.lo) <= Number(p.hi);
  return isIntToken(p.operand);
};

/** date 親の分岐 token が妥当か (正準日付の比較/範囲。範囲は lo<=hi)。 */
const isValidDateBranchToken = (token: string): boolean => {
  const p = parseTokenPredicate(token);
  if (!p) return isValidDate(token); // 素の日付一致は許容
  if (p.kind === "range")
    return isValidDate(p.lo) && isValidDate(p.hi) && p.lo <= p.hi;
  return isValidDate(p.operand);
};

/** 親設問型ごとに cond の token 群が妥当か (作成/更新時の分岐検証)。text は呼び出し側で除外済み。 */
const isValidBranchCond = (parent: Question, values: string[]): boolean => {
  switch (parent.type) {
    case "radio":
    case "checkbox":
      // 素の選択肢値のみ (比較/範囲 token は不可)。親 options に存在すること。
      return values.every(
        (v) =>
          parseTokenPredicate(v) === null &&
          parent.options.some((o) => o.v === v),
      );
    case "scale":
      return values.every(isValidScaleBranchToken);
    case "date":
      return values.every(isValidDateBranchToken);
    default:
      return false;
  }
};

/** 設問定義の妥当性 (scale の min<max・分岐親の型/値)。作成/更新時に検証。 */
export const validateQuestionDefs = (
  questions: Question[],
): ValidationResult => {
  for (const q of questions) {
    if (q.type === "scale" && q.scaleMin >= q.scaleMax)
      return { ok: false, error: `scale min must be < max: ${q.questionId}` };
    const conds = parsePhase(q.phase);
    if (!conds) continue;
    if (conds.length > MAX_PHASE_CONDS)
      return { ok: false, error: `too many branch conditions: ${q.questionId}` };
    const seen = new Set<string>();
    for (const cond of conds) {
      if (cond.parentId === q.questionId)
        return { ok: false, error: `branch parent cannot be itself: ${q.questionId}` };
      if (seen.has(cond.parentId))
        return { ok: false, error: `duplicate branch parent: ${q.questionId}` };
      seen.add(cond.parentId);
      const parent = questions.find((p) => p.questionId === cond.parentId);
      if (!parent)
        return { ok: false, error: `unknown branch parent: ${q.questionId}` };
      if (cond.values.length === 0)
        return { ok: false, error: `branch needs a value: ${q.questionId}` };
      if (parent.type === "text")
        return { ok: false, error: `branch parent must not be text: ${q.questionId}` };
      if (!isValidBranchCond(parent, cond.values))
        return { ok: false, error: `invalid branch condition: ${q.questionId}` };
    }
  }
  return { ok: true };
};

/** NPS (0-10 の scale) を算出。推奨者(9-10)% − 批判者(0-6)%。空なら null。 */
export const npsScore = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const promoters = values.filter((v) => v >= 9).length;
  const detractors = values.filter((v) => v <= 6).length;
  return Math.round(((promoters - detractors) / values.length) * 100);
};

/** 設問ごとに回答を集計する。 */
export const aggregateQuestions = (
  questions: Question[],
  responses: SurveyResponse[],
): QuestionAggregate[] => {
  return questions.map((q): QuestionAggregate => {
    const entries = responses
      .map((r) => ({ r, v: r.answers.find((a) => a.questionId === q.questionId)?.value }))
      .filter((x): x is { r: SurveyResponse; v: AnswerValue } => x.v !== undefined);

    switch (q.type) {
      case "radio": {
        const counts = q.options.map((o) => ({ v: o.v, label: o.label, count: 0 }));
        let total = 0;
        for (const { v } of entries) {
          const rv = radioV(v);
          const c = counts.find((x) => x.v === rv);
          if (c) {
            c.count += 1;
            total += 1;
          }
        }
        return { questionId: q.questionId, type: "radio", counts, total };
      }
      case "checkbox": {
        const counts = q.options.map((o) => ({ v: o.v, label: o.label, count: 0 }));
        let total = 0;
        for (const { v } of entries) {
          if (Array.isArray(v) && v.length > 0) {
            total += 1;
            for (const x of new Set(v)) {
              const c = counts.find((y) => y.v === x);
              if (c) c.count += 1;
            }
          }
        }
        return { questionId: q.questionId, type: "checkbox", counts, total };
      }
      case "text": {
        return {
          questionId: q.questionId,
          type: "text",
          answers: entries
            .filter(({ v }) => typeof v === "string" && v.trim() !== "")
            .map(({ r, v }) => ({
              customerId: r.customerId,
              customerName: r.customerName,
              text: v as string,
            })),
        };
      }
      case "date": {
        return {
          questionId: q.questionId,
          type: "date",
          answers: entries
            .filter(({ v }) => typeof v === "string" && v.trim() !== "")
            .map(({ r, v }) => ({
              customerId: r.customerId,
              customerName: r.customerName,
              date: v as string,
            })),
        };
      }
      case "scale": {
        const values = entries
          .map(({ v }) => v)
          .filter((v): v is number => typeof v === "number");
        const distMap = new Map<number, number>();
        for (let v = q.scaleMin; v <= q.scaleMax; v++) distMap.set(v, 0);
        for (const v of values) distMap.set(v, (distMap.get(v) ?? 0) + 1);
        const average =
          values.length === 0
            ? null
            : values.reduce((s, v) => s + v, 0) / values.length;
        const isNps = q.scaleMin === 0 && q.scaleMax === 10;
        return {
          questionId: q.questionId,
          type: "scale",
          distribution: Array.from(distMap.entries()).map(([value, count]) => ({
            value,
            count,
          })),
          average,
          count: values.length,
          ...(isNps ? { nps: npsScore(values) } : {}),
        };
      }
    }
  });
};

/** 指名割当のうち、対応する顧客回答が未提出のものを返す (open は空)。 */
export const computeUnanswered = (
  assignments: Assignment[],
  responses: SurveyResponse[],
): UnansweredTarget[] => {
  const answered = new Set(responses.map((r) => r.customerId));
  return assignments
    .filter((a) => !answered.has(a.customerId))
    .map((a) => ({
      customerId: a.customerId,
      customerName: a.customerName,
      repUserId: a.repUserId,
      repUserName: a.repUserName,
    }));
};
